import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import CheckoutContext from '../../../../lib/CheckoutContext';
import { useCartPricing } from '../../../../contexts/CartPricingContext';
import { useDropAlreadyRedeemedGwaPromo } from '../../../../contexts/GwaPromoNoticeContext';
import { trackAgentioEmailCapture } from '../../../../lib/analytics/agentio';
import { trackVibeLead } from '../../../../lib/analytics/vibe';
import { setGlobalVariablesForConversionTracking } from '../../../../lib/analytics/analyticsUtils';
import * as events from '../../../../lib/analytics/events';
import { checkEmailExists, stripeCheckout } from '../../../../lib/authentication';
import { reportCheckoutEmailCaptured } from '../../../../lib/checkoutEmailCaptured';
import { requiresCheckoutMarketingConsent } from '../../../../lib/consent';
import {
  StripeCheckoutAccountExistsError,
  ThreeDSecureRequiredError,
  UserFacingError,
  getCustomerMessageFromApolloError,
  getTokenFromApolloError,
  logInternalError,
} from '../../../../lib/errors';
import {
  PendingKlarnaPurchase,
  clearPendingKlarnaPurchase,
  loadPendingKlarnaPurchase,
  savePendingKlarnaPurchase,
} from './klarnaPendingPurchase';
import * as Sentry from '@sentry/react';
import { useActivePromotion } from '../../../../hooks/useActivePromoCode';
import { useMarketingOptIn } from '../../../../contexts/MarketingOptInContext';
import useCheckoutPaths from '../../../../hooks/useCheckoutPaths';
import useTenancy from '../../../../hooks/useTenancy';
import { useLocale } from '../../../../contexts/LocaleContext';
import { gwaPromoForEmailCheck } from '../../../../lib/gwaPromo';
import { toAddressInput } from '../../../../lib/stripeAddress';
import { cartContainsAnyFreeTrialBundle } from '../../../../lib/cart';
import ThankYouLocationState from '../../../../models/ThankYouLocationState';
import * as types from '../../../../types';
import { StripeAddressValue, StripePaymentMethodDetails } from '../../../../components/StripeCheckout';
import { parseStripeAddressName } from '../../../../lib/nameParser';
import { stripeCheckoutBody } from './stripeCheckoutBody';
import { sessionActions } from '../../../../reducers/session';

interface SubmitCheckoutParams {
  tokenId: string;
  email: string;
  address: StripeAddressValue;
  shippingCode: types.ShippingCode;
  paymentMethodDetails: StripePaymentMethodDetails;
  isIdentityVerified: boolean;
  paymentType: events.PaymentType;
  /** payment-mode token: sent with the purchase for recurly to charge inline, not stored first */
  chargedInline: boolean;
  source?: events.CheckoutSurface;
  giftRecipient?: types.gqlTypes.GiftRecipientInput;
  billingAddress?: StripeAddressValue;
}

type FinishedPurchase = Pick<SubmitCheckoutParams, 'email' | 'paymentType' | 'source' | 'giftRecipient'>;

function toPaymentInfo(details: StripePaymentMethodDetails): types.PaymentInfo {
  if (details.type === 'us_bank_account') {
    return {
      __typename: 'BankAccountInfo',
      bankName: details.bankName,
      last4: details.last4,
      accountType: details.accountType,
    };
  }
  if (details.type === 'sepa_debit') {
    return {
      __typename: 'BankAccountInfo',
      bankName: details.bankName,
      last4: details.last4,
      accountType: 'sepa_debit',
    };
  }
  return {
    __typename: 'ObfuscatedCardInfo',
    type: details.brand,
    lastFour: parseInt(details.last4, 10),
    month: details.expMonth,
    year: details.expYear,
  };
}

export default function useStripeCheckout() {
  const { t } = useTranslation();
  const { cart, checkoutState, checkoutActions, executePurchaseMutation } = useContext(CheckoutContext);
  const cartPricing = useCartPricing();
  const { currency, country } = useLocale();
  const dispatch = useDispatch();
  const history = useHistory<ThankYouLocationState>();
  const checkoutPaths = useCheckoutPaths();
  const activePromotion = useActivePromotion();
  const { marketingEmailsOptedIn } = useMarketingOptIn();
  const tenancy = useTenancy();
  const products = useSelector((state: types.AppState) => state.config.products);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [threeDSecureActionToken, setThreeDSecureActionToken] = useState<string | null>(null);
  const [threeDSecurePending, setThreeDSecurePending] = useState(false);
  const [paramsForResubmission, setParamsForResubmission] = useState<SubmitCheckoutParams | null>(null);
  // set when the 3ds action belongs to an inline klarna purchase (redirect flow) rather than the
  // checkout endpoint's card verification — onThreeDSecureToken resubmits the purchase instead
  const [pendingKlarnaPurchase, setPendingKlarnaPurchase] = useState<PendingKlarnaPurchase | null>(null);
  // true while finishing a klarna purchase after the redirect back — the checkout form would
  // otherwise render blank (elements remount empty) while the order completes in the background
  const [resumingPurchase, setResumingPurchase] = useState(false);

  const dropGwaPromo = useDropAlreadyRedeemedGwaPromo();

  // proactive account-exists check — ref so it doesn't cause re-renders
  const knownExistingAccountRef = useRef<{ email: string; hasActiveMembership: boolean } | null>(null);

  // free trial blocker — shown when existing user with active membership has free trials in cart
  const [freeTrialBlocked, setFreeTrialBlocked] = useState(false);
  const clearFreeTrialBlock = useCallback(() => setFreeTrialBlocked(false), []);

  // Abandoned-cart email trigger. On blur rather than on every valid change: Stripe reports the
  // field complete for each valid prefix typed on the way to the final address (jo@acme.co before
  // jo@acme.com), and each of those would otherwise become a Customer.io prospect who gets the email.
  //
  // Where marketing needs consent (GDPR/UK), only a shopper who ticked the opt-in box is reported.
  // The box sits below the email field, so a tick after the blur reports the address at that point.
  const reportCapture = useCallback(
    (email: string) => {
      if (requiresCheckoutMarketingConsent() && !marketingEmailsOptedIn) {
        return;
      }
      void reportCheckoutEmailCaptured({ email, cart, cartPricing, country, currency, activePromotion });
    },
    [cart, cartPricing, country, currency, activePromotion, marketingEmailsOptedIn],
  );
  const reportCaptureRef = useRef(reportCapture);
  reportCaptureRef.current = reportCapture;
  const capturedEmailRef = useRef<string | null>(null);

  const handleEmailBlur = useCallback((email: string) => {
    const trimmed = email.toLowerCase().trim();
    capturedEmailRef.current = trimmed;
    reportCaptureRef.current(trimmed);
  }, []);

  useEffect(() => {
    if (marketingEmailsOptedIn && capturedEmailRef.current) {
      reportCaptureRef.current(capturedEmailRef.current);
    }
  }, [marketingEmailsOptedIn]);

  const handleEmailComplete = useCallback(
    (email: string) => {
      const trimmed = email.toLowerCase().trim();
      void (async () => {
        try {
          const result = await checkEmailExists(trimmed, gwaPromoForEmailCheck(cart.couponCode));
          if (result.gwaPromoRedeemed) {
            dropGwaPromo(cart.couponCode, 'checkout-email');
          }
          if (result.exists) {
            knownExistingAccountRef.current = { email: trimmed, hasActiveMembership: !!result.hasActiveMembership };
            if (result.hasActiveMembership && cartContainsAnyFreeTrialBundle(cart, products)) {
              setFreeTrialBlocked(true);
            }
          } else {
            knownExistingAccountRef.current = null;
            setFreeTrialBlocked(false);
          }
        } catch (err) {
          // 409 fallback at submit still catches existing users, but log so we notice
          // if the endpoint starts consistently failing
          logInternalError(err, {
            tags: { checkoutStep: 'proactiveEmailCheck' },
          });
          knownExistingAccountRef.current = null;
          setFreeTrialBlocked(false);
        }
      })();
    },
    [cart, products, dropGwaPromo],
  );

  // inline login state for existing fi users
  const [existingUserLogin, setExistingUserLogin] = useState<{ email: string } | null>(null);

  const buildCheckoutState = useCallback((params: SubmitCheckoutParams) => {
    const { address, shippingCode, paymentMethodDetails } = params;
    const { firstName, lastName } = parseStripeAddressName(address);
    const shippingAddress = toAddressInput(address);
    const billingAddress = toAddressInput(params.billingAddress ?? address);
    const billingName = parseStripeAddressName(params.billingAddress ?? address);
    const billingInfo: types.BillingInfo = {
      ...billingName,
      address: { ...billingAddress, line2: billingAddress.line2 ?? '' },
      paymentInfo: toPaymentInfo(paymentMethodDetails),
    };

    return { firstName, lastName, shippingAddress, shippingCode, billingInfo };
  }, []);

  const dispatchCheckoutState = useCallback(
    (session: types.Session, params: SubmitCheckoutParams) => {
      const built = buildCheckoutState(params);
      const { firstName, lastName, shippingAddress, shippingCode, billingInfo } = built;

      dispatch(sessionActions.createSession({ session }));
      dispatch(checkoutActions.setCustomerName({ firstName, lastName }));
      dispatch(checkoutActions.setShippingAddress({ shippingAddress }));
      dispatch(checkoutActions.setShippingCode({ shippingCode }));
      dispatch(checkoutActions.setBillingInfo({ billingInfo }));

      events.shipping.continue(cart.cartItems, currency);
      events.payment.continue(cart.cartItems, currency, {
        paymentType: params.paymentType,
        checkoutSurface: params.source,
      });
      trackAgentioEmailCapture();
      trackVibeLead();
      events.checkoutFunnel.infoSubmitted('stripe');

      return built;
    },
    [dispatch, checkoutActions, buildCheckoutState, cart.cartItems, currency],
  );

  const finishPurchase = useCallback(
    (
      result: types.gqlTypes.CartPurchaseResult,
      { email, paymentType, source = 'form', giftRecipient }: FinishedPurchase,
    ) => {
      const { chargeInvoice, giftCardInvoices, isGWP, isReturningCustomer } = result;
      const invoiceNumber = chargeInvoice?.number ?? giftCardInvoices?.[0]?.number;
      const cartItemsArray = Object.values(cart.cartItems);
      setGlobalVariablesForConversionTracking(invoiceNumber, cartItemsArray, cartPricing, email);

      try {
        events.confirmation.finalConfirm(cartItemsArray, cartPricing);
        events.checkoutFunnel.purchaseSucceeded('stripe', source);
        events.orderCompleted(cart, cartPricing, invoiceNumber, undefined, undefined, {
          isReturningCustomer,
          isGWP,
          currency,
          paymentType,
          checkoutSurface: source,
        });
        if (source === 'express') {
          events.payment.expressCheckoutSuccess(paymentType);
        }
      } catch (err) {
        logInternalError(err);
      }

      history.replace(checkoutPaths.ThankYou, {
        orderId: invoiceNumber,
        orderedCart: cart,
        orderedCartPricing: cartPricing,
        giftRecipient,
      });
    },
    [cart, cartPricing, currency, checkoutPaths, history],
  );

  const handleSuccess = useCallback(
    async (session: types.Session, params: SubmitCheckoutParams) => {
      const { firstName, lastName, shippingAddress, shippingCode, billingInfo } = dispatchCheckoutState(
        session,
        params,
      );

      const freshCheckoutState: types.CheckoutState = {
        shippingAddress: {
          line1: shippingAddress.line1,
          line2: shippingAddress.line2 ?? '',
          city: shippingAddress.city,
          state: shippingAddress.state,
          zip: shippingAddress.zip,
          country: shippingAddress.country,
          phone: shippingAddress.phone,
        },
        shippingCode,
        customerName: { firstName, lastName },
        billingInfo,
        giftRecipient: params.giftRecipient,
      };

      // a payment-mode token (klarna, hardware-first wallets) goes to the purchase so recurly
      // charges it inline and saves the billing info from the charge; it was not stored at the
      // checkout endpoint.
      const stripeConfirmationToken = params.chargedInline ? params.tokenId : undefined;
      try {
        const result = await executePurchaseMutation(cart, freshCheckoutState, undefined, stripeConfirmationToken);
        finishPurchase(result, params);
      } catch (err) {
        const actionTokenId = params.paymentType === 'klarna' ? getTokenFromApolloError(err) : undefined;
        if (!actionTokenId) {
          throw err;
        }
        // klarna requires customer approval on klarna's site — recurly surfaces this as a 3ds
        // action. running the action redirects the whole page to klarna, so persist what the
        // return needs to resume the purchase (cart/session/checkout state are already in
        // redux persistence).
        const pending: PendingKlarnaPurchase = {
          actionTokenId,
          stripeConfirmationToken: params.tokenId,
          email: params.email,
          createdAt: Date.now(),
        };
        savePendingKlarnaPurchase(pending);
        setPendingKlarnaPurchase(pending);
        setThreeDSecureActionToken(actionTokenId);
        setThreeDSecurePending(true);
        events.payment.threeDSecurePrompt();
      }
    },
    [dispatchCheckoutState, executePurchaseMutation, cart, finishPurchase],
  );

  const completeKlarnaPurchase = useCallback(
    async (pending: PendingKlarnaPurchase, actionResultTokenId: string) => {
      setSubmitting(true);
      setError(undefined);
      setThreeDSecureActionToken(null);

      try {
        // resubmit with the action result token plus the original klarna confirmation token —
        // recurly needs both to complete the pending transaction. checkout state was dispatched
        // to redux before the first purchase attempt, so it's intact even after the redirect.
        const result = await executePurchaseMutation(
          cart,
          checkoutState,
          actionResultTokenId,
          pending.stripeConfirmationToken,
        );
        clearPendingKlarnaPurchase();
        events.payment.threeDSecureSuccess();
        finishPurchase(result, { email: pending.email, paymentType: 'klarna' });
      } catch (err) {
        clearPendingKlarnaPurchase();
        const customerMessage = getCustomerMessageFromApolloError(err);
        const message = customerMessage ?? t('something_went_wrong');
        events.payment.continueError(message);
        if (!customerMessage) {
          logInternalError(err, {
            fingerprint: ['{{ default }}', err instanceof Error ? err.message : 'unknown'],
            tags: { checkoutStep: 'stripeCheckoutKlarnaResume' },
          });
        }
        setError(message);
      } finally {
        setSubmitting(false);
        setThreeDSecurePending(false);
        setPendingKlarnaPurchase(null);
        setResumingPurchase(false);
      }
    },
    [t, cart, checkoutState, executePurchaseMutation, finishPurchase],
  );

  const executeCheckout = useCallback(
    async (params: SubmitCheckoutParams, threeDSecureActionResultToken?: string) => {
      const { address } = params;

      // guard against empty address fields — if the address data wasn't captured correctly,
      // fail early with a user-facing error instead of sending incomplete data to recurly.
      // state/county is only required for US, CA, and ES — other countries (e.g. GB) don't use it.
      const requiresState = ['US', 'CA', 'ES'].includes(address.address.country);
      if (!address.address.line1 || !address.address.city || (requiresState && !address.address.state)) {
        logInternalError(new Error('stripe checkout address fields empty at submission'), {
          fingerprint: ['stripe-checkout-empty-address'],
          tags: { checkoutStep: 'stripeCheckoutAddressValidation' },
          extra: {
            hasLine1: !!address.address.line1,
            hasCity: !!address.address.city,
            hasState: !!address.address.state,
            hasPostalCode: !!address.address.postal_code,
            hasCountry: !!address.address.country,
          },
        });
        throw new UserFacingError(t('a_shipping_address_is_required'));
      }

      const session = await stripeCheckout(stripeCheckoutBody(params, tenancy, threeDSecureActionResultToken));

      return session;
    },
    [t, tenancy],
  );

  const submitCheckout = useCallback(
    async (params: SubmitCheckoutParams) => {
      // short-circuit: if we proactively detected an existing account and the payment
      // method is plain card (not identity-verified), show inline login immediately
      // without hitting the stripe-checkout endpoint. wallet/link users proceed
      // normally — the server auto-logins them.
      const known = knownExistingAccountRef.current;
      if (known && !params.isIdentityVerified && known.email === params.email.toLowerCase().trim()) {
        setParamsForResubmission(params);
        setExistingUserLogin({ email: known.email });
        return;
      }

      setSubmitting(true);
      setError(undefined);

      try {
        const session = await executeCheckout(params);
        await handleSuccess(session, params);
      } catch (err) {
        if (err instanceof StripeCheckoutAccountExistsError) {
          events.payment.continueError(err.message);

          const { email } = params;
          const { firstName, lastName, shippingAddress, shippingCode, billingInfo } = buildCheckoutState(params);

          dispatch(checkoutActions.setCustomerName({ firstName, lastName }));
          dispatch(checkoutActions.setShippingAddress({ shippingAddress }));
          dispatch(checkoutActions.setShippingCode({ shippingCode }));
          dispatch(checkoutActions.setBillingInfo({ billingInfo }));

          // inline login - show the login form on the same page and retry after auth
          setParamsForResubmission(params);
          setExistingUserLogin({ email });
        } else if (err instanceof ThreeDSecureRequiredError) {
          events.payment.threeDSecurePrompt();
          setThreeDSecureActionToken(err.actionTokenId);
          setThreeDSecurePending(true);
          setParamsForResubmission(params);
        } else if (err instanceof UserFacingError) {
          events.payment.continueError(err.message);
          if (params.source === 'express') {
            events.payment.expressCheckoutError(params.paymentType, err.message);
          }
          setError(err.message);
        } else {
          const customerMessage = getCustomerMessageFromApolloError(err);
          const message = customerMessage ?? t('something_went_wrong');
          events.payment.continueError(message);
          if (params.source === 'express') {
            events.payment.expressCheckoutError(params.paymentType, message);
          }
          if (!customerMessage) {
            logInternalError(err, {
              fingerprint: ['{{ default }}', err instanceof Error ? err.message : 'unknown'],
              tags: { checkoutStep: 'stripeCheckout' },
            });
          }
          setError(message);
        }
      } finally {
        setSubmitting(false);
      }
    },
    [t, executeCheckout, handleSuccess, dispatch, checkoutActions, buildCheckoutState],
  );

  const onThreeDSecureToken = useCallback(
    async (token: { id: string }) => {
      if (pendingKlarnaPurchase) {
        await completeKlarnaPurchase(pendingKlarnaPurchase, token.id);
        return;
      }

      Sentry.captureMessage('3DS challenge succeeded (billing step)', {
        level: 'info',
        tags: {
          checkoutStep: 'stripeCheckout3DSecure',
          threeDSecureResult: 'success',
        },
      });

      if (!paramsForResubmission) {
        setError(t('something_went_wrong'));
        setThreeDSecurePending(false);
        return;
      }

      setSubmitting(true);
      setError(undefined);
      setThreeDSecureActionToken(null);

      try {
        const session = await executeCheckout(paramsForResubmission, token.id);
        events.payment.threeDSecureSuccess();
        await handleSuccess(session, paramsForResubmission);
      } catch (err) {
        if (err instanceof UserFacingError) {
          events.payment.continueError(err.message);
          setError(err.message);
        } else {
          const customerMessage = getCustomerMessageFromApolloError(err);
          const message = customerMessage ?? t('something_went_wrong');
          events.payment.continueError(message);
          if (!customerMessage) {
            logInternalError(err, {
              fingerprint: ['{{ default }}', err instanceof Error ? err.message : 'unknown'],
              tags: { checkoutStep: 'stripeCheckout3DSResubmit' },
            });
          }
          setError(message);
        }
      } finally {
        setSubmitting(false);
        setThreeDSecurePending(false);
        setParamsForResubmission(null);
      }
    },
    [t, paramsForResubmission, executeCheckout, handleSuccess, pendingKlarnaPurchase, completeKlarnaPurchase],
  );

  const onExistingUserLogin = useCallback(
    async (session: types.Session) => {
      if (!paramsForResubmission) {
        setError(t('something_went_wrong'));
        return;
      }

      setSubmitting(true);
      setError(undefined);
      setExistingUserLogin(null);

      // store login session so stripeCheckout retries as an authenticated user
      dispatch(sessionActions.createSession({ session }));

      try {
        // retry checkout now that user is authenticated
        const retrySession = await executeCheckout(paramsForResubmission);
        await handleSuccess(retrySession, paramsForResubmission);
      } catch (retryErr) {
        if (retryErr instanceof UserFacingError) {
          events.payment.continueError(retryErr.message);
          setError(retryErr.message);
        } else {
          const customerMessage = getCustomerMessageFromApolloError(retryErr);
          const message = customerMessage ?? t('something_went_wrong');
          events.payment.continueError(message);
          if (!customerMessage) {
            logInternalError(retryErr, {
              fingerprint: ['{{ default }}', retryErr instanceof Error ? retryErr.message : 'unknown'],
              tags: { checkoutStep: 'stripeCheckoutExistingUserRetry' },
            });
          }
          setError(message);
        }
      } finally {
        setSubmitting(false);
        setParamsForResubmission(null);
      }
    },
    [t, dispatch, paramsForResubmission, executeCheckout, handleSuccess],
  );

  const onThreeDSecureError = useCallback((e: { message: string }) => {
    const isVendorLoadError = (e as any).code === '3ds-vendor-load-error';
    logInternalError(e instanceof Error ? e : new Error(e.message), {
      fingerprint: ['{{ default }}', e.message],
      tags: {
        checkoutStep: 'stripeCheckout3DSecure',
        threeDSecureResult: 'error',
        ...(isVendorLoadError && { threeDSecureVendorLoadError: 'true' }),
      },
    });
    events.payment.threeDSecureError(e.message);
    clearPendingKlarnaPurchase();
    setPendingKlarnaPurchase(null);
    setResumingPurchase(false);
    setError(e.message);
    setThreeDSecurePending(false);
    setThreeDSecureActionToken(null);
    setParamsForResubmission(null);
  }, []);

  // resume an inline klarna purchase after the redirect back from klarna. re-running the 3ds
  // action with the original action token makes recurly.js fall back to retrieving the (now
  // succeeded) payment intent and mint the action result token, which completes the purchase.
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const redirectStatus = searchParams.get('redirect_status');
    if (!redirectStatus) {
      return;
    }
    const pending = loadPendingKlarnaPurchase();
    // strip stripe's redirect params so a reload doesn't re-trigger the resume
    history.replace({ pathname: window.location.pathname });
    if (!pending) {
      return;
    }
    if (redirectStatus === 'succeeded') {
      setPendingKlarnaPurchase(pending);
      setThreeDSecureActionToken(pending.actionTokenId);
      setThreeDSecurePending(true);
      setResumingPurchase(true);
    } else {
      clearPendingKlarnaPurchase();
      events.payment.continueError(`klarna redirect returned ${redirectStatus}`);
      setError(t('your_payment_was_not_completed'));
    }
    // mount-only: the redirect params and pending record exist exactly once, on return
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    submitCheckout,
    submitting,
    error,
    resumingPurchase,
    threeDSecureActionToken,
    threeDSecurePending,
    onThreeDSecureToken,
    onThreeDSecureError,
    existingUserLogin,
    onExistingUserLogin,
    handleEmailComplete,
    handleEmailBlur,
    freeTrialBlocked,
    clearFreeTrialBlock,
  };
}
